<div class="cumulative-charts-page">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">
        <i class="fas fa-chart-line mr-3"></i>
        Analytics Dashboard
      </h1>
      <p class="page-subtitle">Comprehensive overview of business metrics and trends</p>
    </div>
    <div class="header-actions">
      <button (click)="exportToCSV()" [disabled]="!cumulativeData?.entityData?.length" class="export-btn">
        <i class="fas fa-download mr-2"></i>
        Export CSV
      </button>
    </div>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-card">
      <!-- Date Range Picker -->
      <div class="filter-row">
        <app-date-range-picker [showGranularity]="true" [initialPreset]="'last30days'"
          [initialGranularity]="granularity" (dateRangeChange)="onDateRangeChange($event)"
          (granularityChange)="onGranularityChange($event)">
        </app-date-range-picker>
      </div>

      <!-- Additional Filters -->
      <div class="filter-row secondary-filters">
        <div class="filter-group">
          <label class="filter-label">Geo Zone / District</label>
          <app-district-zone-filter
            [(ngModel)]="selectedGeoZones"
            (ngModelChange)="applyFilters()">
          </app-district-zone-filter>
        </div>

        <div class="filter-group">
          <label class="filter-label">Agent</label>
          <div class="status-dropdown-container">
            <button
              type="button"
              id="agentFilter"
              (click)="toggleAgentDropdown()"
              class="status-dropdown-button"
              [class.active]="agentDropdownOpen || selectedAgents.length > 0">
              <span>{{ getAgentDisplayText() }}</span>
              <i class="fas" [class.fa-chevron-down]="!agentDropdownOpen" [class.fa-chevron-up]="agentDropdownOpen"></i>
            </button>
            <div class="status-dropdown-menu" *ngIf="agentDropdownOpen">
              <div class="status-dropdown-header">
                <span>Select Agent(s)</span>
                <button 
                  type="button"
                  (click)="selectedAgents = []; applyFilters(); $event.stopPropagation()"
                  class="clear-status-btn"
                  *ngIf="selectedAgents.length > 0">
                  Clear All
                </button>
              </div>
              <div class="status-options">
                <label 
                  *ngFor="let agent of agents" 
                  class="status-option-item"
                  (click)="$event.stopPropagation()">
                  <input
                    type="checkbox"
                    [checked]="isAgentSelected(agent.value)"
                    (change)="toggleAgent(agent.value); applyFilters()"
                    class="status-checkbox">
                  <span class="status-label">{{ agent.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <button (click)="resetFilters()" class="reset-btn">
          <i class="fas fa-redo mr-1"></i>
          Reset
        </button>
      </div>
    </div>
  </div>

  <!-- Chart View Toggle -->
  <div class="view-toggle">
    <span class="toggle-label">View:</span>
    <button (click)="chartViewType = 'combined'" [class.active]="chartViewType === 'combined'" class="toggle-btn">
      <i class="fas fa-layer-group mr-1"></i>
      Combined
    </button>
    <button (click)="chartViewType = 'individual'" [class.active]="chartViewType === 'individual'" class="toggle-btn">
      <i class="fas fa-th-large mr-1"></i>
      Individual
    </button>
    <button (click)="chartViewType = 'cumulative'" [class.active]="chartViewType === 'cumulative'" class="toggle-btn">
      <i class="fas fa-chart-area mr-1"></i>
      Running Total
    </button>
    <button (click)="chartViewType = 'insights'" [class.active]="chartViewType === 'insights'"
      class="toggle-btn insights-btn">
      <i class="fas fa-coins mr-1"></i>
      Revenue Insights
    </button>
  </div>

  <!-- Charts Section -->
  <div class="charts-section">
    <!-- Loading State -->
    <div *ngIf="loadingCumulative" class="charts-loading">
      <div class="loading-spinner large"></div>
      <span>Loading charts...</span>
    </div>

    <!-- Error State -->
    <div *ngIf="errorCumulative && !loadingCumulative" class="charts-error">
      <i class="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
      <h3>Failed to Load Charts</h3>
      <p>{{ errorCumulative }}</p>
      <button (click)="retryCumulativeLoad()" class="retry-btn primary">
        <i class="fas fa-redo mr-2"></i>
        Try Again
      </button>
    </div>

    <!-- Combined View -->
    <div *ngIf="!loadingCumulative && !errorCumulative && chartViewType === 'combined'" class="combined-chart">
      <div class="chart-card full-width">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <i class="fas fa-chart-area mr-2 text-green-600"></i>
            Daily Trends
          </h3>
          <span class="granularity-badge">{{ getGranularityLabel() }}</span>
        </div>
        <app-multi-series-chart [dataSeries]="getTimeSeriesEntities()" [title]="''" [type]="'line'" [height]="400"
          [loading]="loadingCumulative" [error]="errorCumulative" [fill]="true" [showLegend]="true"
          [retryCallback]="retryCumulativeLoad">
        </app-multi-series-chart>
      </div>
    </div>

    <!-- Individual Charts View -->
    <div *ngIf="!loadingCumulative && !errorCumulative && chartViewType === 'individual'" class="individual-charts">
      <div *ngFor="let entity of getTimeSeriesEntities(); let i = index; trackBy: trackByEntityName" class="chart-card">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <span class="entity-indicator" [style.backgroundColor]="getEntityColor(i)"></span>
            {{ entity.entityName }}
          </h3>
          <div class="chart-meta">
            <span class="total-badge">
              Total: {{ entity.totalCount | number }}
            </span>
          </div>
        </div>
        <app-chart-wrapper [data]="entity" [type]="'bar'" [colors]="[getEntityColor(i)]" [height]="280"
          [loading]="false" [error]="''" [showLegend]="false" [fill]="true">
        </app-chart-wrapper>
      </div>

      <!-- Empty State -->
      <div *ngIf="getTimeSeriesEntities().length === 0" class="empty-state">
        <i class="fas fa-chart-pie text-gray-300 text-5xl mb-4"></i>
        <h3>No Chart Data Available</h3>
        <p>Try adjusting your filters or date range to see data.</p>
      </div>
    </div>

    <!-- Cumulative Running Total View -->
    <div *ngIf="!loadingCumulative && !errorCumulative && chartViewType === 'cumulative'" class="cumulative-charts">
      <!-- Combined Running Total Chart -->
      <div class="chart-card full-width mb-6">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <i class="fas fa-chart-area mr-2 text-blue-600"></i>
            Running Total Overview
          </h3>
          <span class="cumulative-badge">Cumulative</span>
        </div>
        <app-multi-series-chart [dataSeries]="getCumulativeRunningTotalData()" [title]="''" [type]="'line'"
          [height]="350" [loading]="loadingCumulative" [error]="errorCumulative" [fill]="true" [showLegend]="true"
          [retryCallback]="retryCumulativeLoad">
        </app-multi-series-chart>
      </div>

      <!-- Individual Running Total Charts -->
      <div class="individual-charts">
        <div *ngFor="let entity of getCumulativeRunningTotalData(); let i = index; trackBy: trackByEntityName"
          class="chart-card">
          <div class="chart-card-header">
            <h3 class="chart-title">
              <span class="entity-indicator" [style.backgroundColor]="getEntityColor(i)"></span>
              {{ entity.entityName }}
            </h3>
            <div class="chart-meta">
              <span class="cumulative-badge small">Running Total</span>
            </div>
          </div>
          <app-chart-wrapper [data]="entity" [type]="'line'" [colors]="[getEntityColor(i)]" [height]="280"
            [loading]="false" [error]="''" [showLegend]="false" [fill]="true">
          </app-chart-wrapper>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="getCumulativeRunningTotalData().length === 0" class="empty-state">
        <i class="fas fa-chart-area text-gray-300 text-5xl mb-4"></i>
        <h3>No Cumulative Data Available</h3>
        <p>Appointment and Scrap Collection data will appear here.</p>
      </div>
    </div>

    <!-- Revenue Insights View -->
    <div *ngIf="!loadingInsights && !errorInsights && chartViewType === 'insights'" class="insights-view">

      <!-- Daily Revenue Trends -->
      <div class="chart-card full-width mb-6">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <i class="fas fa-chart-line mr-2 text-green-600"></i>
            Daily Collection Trends
          </h3>
          <span class="granularity-badge">{{ getGranularityLabel() }}</span>
        </div>
        <app-multi-series-chart [dataSeries]="getInsightsDailyChartData()" [title]="''" [type]="'line'" [height]="350"
          [loading]="loadingInsights" [error]="errorInsights" [fill]="false" [showLegend]="true"
          [retryCallback]="retryInsightsLoad">
        </app-multi-series-chart>
      </div>

      <!-- Revenue Breakdown -->
      <div class="chart-card full-width mb-6">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <i class="fas fa-coins mr-2 text-amber-500"></i>
            Revenue Breakdown
          </h3>
          <span class="revenue-badge">Daily</span>
        </div>
        <app-multi-series-chart [dataSeries]="getInsightsRevenueBreakdownData()" [title]="''" [type]="'bar'"
          [height]="300" [loading]="loadingInsights" [error]="errorInsights" [fill]="true" [stacked]="true"
          [showLegend]="true" [retryCallback]="retryInsightsLoad">
        </app-multi-series-chart>
      </div>

      <!-- Cumulative Growth -->
      <div class="chart-card full-width">
        <div class="chart-card-header">
          <h3 class="chart-title">
            <i class="fas fa-chart-area mr-2 text-blue-600"></i>
            Cumulative Growth
          </h3>
          <span class="cumulative-badge">Running Total</span>
        </div>
        <app-multi-series-chart [dataSeries]="getInsightsCumulativeChartData()" [title]="''" [type]="'line'"
          [height]="350" [loading]="loadingInsights" [error]="errorInsights" [fill]="true" [showLegend]="true"
          [retryCallback]="retryInsightsLoad">
        </app-multi-series-chart>
      </div>

      <!-- Empty State -->
      <div *ngIf="!insightsData?.dataPoints?.length" class="empty-state">
        <i class="fas fa-chart-pie text-gray-300 text-5xl mb-4"></i>
        <h3>No Insights Data Available</h3>
        <p>Scrap collection revenue data will appear here.</p>
      </div>
    </div>

    <!-- Insights Loading State -->
    <div *ngIf="loadingInsights && chartViewType === 'insights'" class="charts-loading">
      <div class="loading-spinner large"></div>
      <span>Loading insights...</span>
    </div>

    <!-- Insights Error State -->
    <div *ngIf="errorInsights && !loadingInsights && chartViewType === 'insights'" class="charts-error">
      <i class="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
      <h3>Failed to Load Insights</h3>
      <p>{{ errorInsights }}</p>
      <button (click)="retryInsightsLoad()" class="retry-btn primary">
        <i class="fas fa-redo mr-2"></i>
        Try Again
      </button>
    </div>
  </div>

  <!-- Additional Info Footer -->
  <div class="info-footer" *ngIf="cumulativeData">
    <div class="info-item">
      <i class="fas fa-calendar mr-2 text-gray-400"></i>
      <span>
        Data from {{ cumulativeData.startDate | date:'mediumDate' }}
        to {{ cumulativeData.endDate | date:'mediumDate' }}
      </span>
    </div>
    <div class="info-item">
      <i class="fas fa-clock mr-2 text-gray-400"></i>
      <span>Last updated: {{ currentTime | date:'short' }}</span>
    </div>
  </div>
</div>